/**
 * Regras puras do projetista do executivo (sem React, sem rede).
 *
 * A atribuição mora no backend; aqui fica o que só a planilha sabe: se o
 * ambiente já chegou no executivo, se a identidade dele é confiável e como
 * as datas "AAAA-MM-DD" do backend viram dia local.
 */
import { chaveAmbiente, rotuloAmbiente } from "../../pages/MapaProducao/dados/chaves";
import { hasValue, isNo } from "../../utils/data";
import { F } from "../../utils/gerencialFields";
import { bloqueioDoComentario } from "../../utils/identidadeAmbiente";

export const SEM_PROJETISTA = "Sem projetista";

/** O ambiente está na fila do executivo ou já passou por ela (pré-requisitos prontos). */
export function chegouNoExecutivo(row) {
  if (!row) return false;
  return (
    isNo(row[F.OBRA_FUTURA]) &&
    hasValue(row[F.LIBERACAO_DA_OBRA]) &&
    hasValue(row[F.DEFINICAO_ACABAMENTOS_REALIZADO]) &&
    hasValue(row[F.MEDICAO_REALIZADO])
  );
}

export function executivoFinalizado(row) {
  return hasValue(row?.[F.EXECUTIVO_REALIZADO]);
}

/** "2026-10-02" → Date à meia-noite LOCAL (new Date("2026-10-02") seria UTC: dia anterior em SP). */
export function dataLocal(iso) {
  const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
  if (!partes) return null;
  return new Date(Number(partes[1]), Number(partes[2]) - 1, Number(partes[3]));
}

export function isoDoDia(data = new Date()) {
  const dois = (n) => String(n).padStart(2, "0");
  return `${data.getFullYear()}-${dois(data.getMonth() + 1)}-${dois(data.getDate())}`;
}

/** "02/10/2026" a partir de "2026-10-02" ("" quando vazio). */
export function dataBR(iso) {
  const data = dataLocal(iso);
  return data ? data.toLocaleDateString("pt-BR") : "";
}

/** `{chave, rotulo}` como o backend espera, ou null sem identidade. */
export function itemDoAmbiente(row) {
  const chave = chaveAmbiente(row);
  return chave ? { chave, rotulo: rotuloAmbiente(row) } : null;
}

/**
 * Por que este ambiente não pode receber projetista (texto pronto para a
 * tela) ou null. Mesmos casos do histórico de comentários: sem pedido/ambiente,
 * pedido em mais de um cliente, ambiente repetido no pedido.
 */
export function bloqueioDoProjetista(row, indice) {
  const bloqueio = bloqueioDoComentario(row, indice);
  if (!bloqueio) return null;
  const MENSAGENS = {
    sem_chave: "Preencha o NUM. PEDIDO e o AMBIENTE desta linha na planilha para definir o projetista.",
    pedido_com_varios_clientes:
      "Este número de pedido aparece na planilha para mais de um cliente. Corrija na planilha para definir o projetista.",
    ambiente_repetido:
      "Há mais de um ambiente com este nome no pedido. Diferencie os nomes na planilha para definir o projetista.",
  };
  return MENSAGENS[bloqueio.motivo] || bloqueio.mensagem;
}

/** Atribuição do ambiente (ou null) no mapa `{chave: atribuicao}` do backend. */
export function atribuicaoDe(row, atribuicoes) {
  const chave = chaveAmbiente(row);
  return (chave && atribuicoes?.[chave]) || null;
}

/**
 * Carga por projetista nas linhas da tela: `[{nome, total}]`, do mais
 * carregado ao menos, com "Sem projetista" por último (só quando há).
 */
export function resumoDeCarga(linhas = []) {
  const contagem = new Map();
  let vagos = 0;
  linhas.forEach((linha) => {
    if (!linha.projetista) {
      vagos += 1;
      return;
    }
    contagem.set(linha.projetista, (contagem.get(linha.projetista) || 0) + 1);
  });
  const carga = [...contagem.entries()]
    .map(([nome, total]) => ({ nome, total }))
    .sort((a, b) => b.total - a.total || a.nome.localeCompare(b.nome, "pt-BR"));
  return vagos ? [...carga, { nome: SEM_PROJETISTA, total: vagos }] : carga;
}

/**
 * As passagens do ambiente para a linha do tempo, da mais nova para a mais
 * antiga. A atual termina na data de EXECUTIVO REALIZADO da planilha (`fim`
 * nulo enquanto o executivo não foi finalizado).
 */
export function passagensDoAmbiente(atribuicao, executivoRealizado = null) {
  if (!atribuicao) return [];
  const anteriores = (atribuicao.anteriores || []).map((p) => ({ ...p, atual: false }));
  const atual = atribuicao.projetista_id
    ? [{
        projetista_id: atribuicao.projetista_id,
        projetista: atribuicao.projetista,
        inicio: atribuicao.inicio,
        fim: executivoRealizado,
        atual: true,
      }]
    : [];
  return [...atual, ...anteriores.reverse()];
}
